import * as React from "react";
import { createPortal } from "react-dom";
import useGeoCountry from "../hooks/useGeoCountry";
import useDrupalSettings from "../hooks/useDrupalSettings";
import useHreflangAlternates from "../hooks/useHreflangAlternates";

function isDismissed(id) {
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem(`geo-banner-dismissed:${id}`) === "1";
}

function dismiss(id) {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(`geo-banner-dismissed:${id}`, "1");
}

// Builds Drupal's locale-prefixed settings URL, e.g.
// https://cms.example.com/fr/api/drupal-config — no prefix for
// defaultLanguage, matching the same convention your Gatsby language
// switcher already uses for page URLs.
function buildSettingsUrl(drupalBaseUrl, configUrl, pageLanguage, defaultLanguage) {
    if (!drupalBaseUrl) return null;

    const path = configUrl;
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const base = drupalBaseUrl.replace(/\/$/, "");
    const prefix =
        pageLanguage && pageLanguage !== defaultLanguage ? `/${pageLanguage}` : "";

    return `${base}${prefix}${normalizedPath}`;
}

// Finds the alternate entry that IS the current page, so we know the
// current page's own region (its country) without any separate config for
// it. Only region is used from this now — language comes from the
// authoritative pageLanguage prop instead (see resolveRedirectTarget).
function findSelf(alternates) {
    if (typeof window === "undefined" || alternates.length === 0) return null;

    const currentHref = window.location.href;
    const exact = alternates.find((a) => a.href === currentHref);
    if (exact) return exact;

    // Loose fallback in case of trailing-slash / protocol mismatches between
    // the hreflang href and window.location.href.
    return (
        alternates.find((a) => {
            try {
                const u = new URL(a.href);
                return (
                    u.hostname === window.location.hostname &&
                    u.pathname.replace(/\/$/, "") ===
                    window.location.pathname.replace(/\/$/, "")
                );
            } catch {
                return false;
            }
        }) || null
    );
}

function resolveRedirectTarget(alternates, country, pageLanguage) {
    if (!country || alternates.length === 0) return null;

    const self = findSelf(alternates);
    if (self && self.region === country) return null; // already on the right site

    const candidates = alternates.filter((a) => a.region === country);
    if (candidates.length === 0) return null;

    const preferred =
        candidates.find((c) => c.lang === pageLanguage) ||
        (self && candidates.find((c) => c.lang === self.lang)) ||
        candidates[0];

    return { ...preferred, self };
}

function countryDisplayName(regionCode, localeHint) {
    if (!regionCode) return regionCode;
    try {
        const displayNames = new Intl.DisplayNames([localeHint || "en"], {
            type: "region",
        });
        return displayNames.of(regionCode) || regionCode;
    } catch {
        // Intl.DisplayNames isn't available in every runtime (e.g. very old
        // browsers) — fall back to the raw ISO code rather than breaking.
        return regionCode;
    }
}

function fillTokens(template, currentCountryName, regionCountryName) {
    if (!template) return template;
    return template
        .split("@country")
        .join(currentCountryName)
        .split("@country")
        .join(regionCountryName);
}

/**
 * Shows at most one "you're visiting from {country} — go to the {country}
 * site instead?" prompt.
 *
 * - Drupal (called directly, cross-origin) supplies only copy: a `text`
 *   template with "@current_country" / "@region_country" tokens, plus
 *   "stay" / "leave" button labels. No country list, no URLs. The request
 *   goes to a locale-prefixed path (e.g. /fr/api/drupal-config) based on
 *   `pageLanguage`, so the copy comes back already translated.
 * - The destination is resolved ENTIRELY from the current page's own
 *   <link rel="alternate" hreflang="xx-YY"> tags — nothing hardcoded, no
 *   other data source. Match the visitor's detected country against the
 *   region subtag, preferring a candidate that also shares the current
 *   page's language when a country has more than one (e.g. en-CA vs
 *   fr-CA).
 * - Renders nothing if there's no alternate for the visitor's country on
 *   this page, or if they're already on it. If a page is missing an
 *   alternate for a market that does have a real site, that page's
 *   hreflang tags need fixing in Drupal — see resolveRedirectTarget's
 *   comment for why this deliberately doesn't compensate with a hardcoded
 *   fallback.
 * - If `injectSelector` is set, renders via a React portal into the first
 *   DOM element matching that CSS selector (e.g. your existing
 *   `<header>`), at `injectPosition` ("start" or "end") — instead of
 *   rendering in-place. This lets the plugin's default auto-injection
 *   (still just wrapPageElement, no site code changes) land inside an
 *   existing element rather than always at the very top of the page.
 * - Without `injectSelector`, exposes its own height as the CSS custom
 *   property `--geo-banner-height` on <html>, so the site's own
 *   sticky/fixed header can stack directly beneath it
 *   (`top: var(--geo-banner-height, 0px)`) instead of both competing for
 *   `top: 0`. See the README — this coordination isn't needed when using
 *   `injectSelector` to place the banner inside the header itself, since
 *   it then scrolls/sticks together with the header as one element.
 */
export default function GeoBanner(props) {
    const cookieName = props.cookieName || "geo_country";
    const drupalBaseUrl = props.drupalBaseUrl;
    const configUrl = props.configUrl;
    const defaultLanguage = props.defaultLanguage || "en";
    const pageLanguage = props.pageLanguage || defaultLanguage;
    const apiKey = props.apiKey;
    const locationKey = props.locationKey;
    const injectSelector = props.injectSelector;
    const injectPosition = props.injectPosition === "end" ? "end" : "start";

    const settingsUrl = buildSettingsUrl(
        drupalBaseUrl,
        configUrl,
        pageLanguage,
        defaultLanguage
    );

    const country = useGeoCountry(cookieName);
    const settings = useDrupalSettings(settingsUrl, apiKey);
    const alternates = useHreflangAlternates(locationKey);
    const [, forceRerender] = React.useState(0);
    const rootRef = React.useRef(null);

    const target =
        settings.status === "ready" && settings.text && country
            ? resolveRedirectTarget(alternates, country, pageLanguage)
            : null;

    const dismissalId = target ? `${target.region}:${target.lang || ""}` : null;
    const shouldRender = Boolean(target) && !isDismissed(dismissalId);

    // Only used without injectSelector: coordinates with the site's own
    // sticky/fixed header via a CSS variable (see docblock above).
    React.useEffect(() => {
        if (typeof document === "undefined" || injectSelector) return undefined;
        const root = document.documentElement;

        if (!shouldRender || !rootRef.current) {
            root.style.setProperty("--geo-banner-height", "0px");
            return undefined;
        }

        const el = rootRef.current;
        const updateHeight = () => {
            root.style.setProperty("--geo-banner-height", `${el.offsetHeight}px`);
        };
        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(el);

        return () => {
            resizeObserver.disconnect();
            root.style.setProperty("--geo-banner-height", "0px");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- re-measure
        // whenever visibility or the underlying message/target changes.
    }, [injectSelector, shouldRender, target && target.href]);

    const [portalNode, setPortalNode] = React.useState(null);

    React.useEffect(() => {
        if (typeof document === "undefined" || !injectSelector) {
            setPortalNode(null);
            return undefined;
        }

        let wrapper = null;
        let attachedTo = null;

        const attach = () => {
            const target2 = document.querySelector(injectSelector);
            if (!target2 || target2 === attachedTo) return;

            wrapper = document.createElement("div");
            wrapper.setAttribute("data-geo-banner-portal", "");
            if (injectPosition === "end") {
                target2.appendChild(wrapper);
            } else {
                target2.insertBefore(wrapper, target2.firstChild);
            }
            attachedTo = target2;
            setPortalNode(wrapper);
        };

        attach();

        const observer = new MutationObserver(attach);
        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
            if (wrapper && wrapper.parentNode) {
                wrapper.parentNode.removeChild(wrapper);
            }
            setPortalNode(null);
        };
    }, [injectSelector, injectPosition, locationKey]);

    if (!shouldRender) return null;
    if (injectSelector && !portalNode) return null; // target not found yet

    const localeHint =
        pageLanguage ||
        (target.self && target.self.lang) ||
        (typeof navigator !== "undefined" && navigator.language) ||
        "en";
    // Same value for both today (see fillTokens' comment) — visitor's
    // detected country and the matched alternate's country are guaranteed
    // equal by resolveRedirectTarget's exact-match-only rule.
    const countryName = countryDisplayName(target.region, localeHint);
    const message = fillTokens(settings.text, countryName, countryName);

    const handleConfirm = () => {
        if (typeof window !== "undefined") {
            window.location.assign(target.href);
        }
    };

    const handleDismiss = () => {
        dismiss(dismissalId);
        forceRerender((n) => n + 1);
    };

    const bannerElement = React.createElement(
        "div",
        {
            ref: rootRef,
            className: injectSelector ? "geo-banner geo-banner--embedded" : "geo-banner",
            role: "region",
            "aria-label": "Site notice",
        },
        React.createElement(
            "p",
            { key: "message", className: "geo-banner__message" },
            message
        ),
        React.createElement(
            "div",
            { key: "actions", className: "geo-banner__actions" },
            React.createElement(
                "button",
                {
                    type: "button",
                    className: "geo-banner__dismiss",
                    onClick: handleDismiss,
                },
                settings.stayLabel || "Stay"
            ),
            React.createElement(
                "button",
                {
                    type: "button",
                    className: "geo-banner__confirm",
                    onClick: handleConfirm,
                },
                settings.leaveLabel || "Leave"
            ),
        )
    );

    return injectSelector ? createPortal(bannerElement, portalNode) : bannerElement;
}